import { useCallback, useMemo } from 'react';

import { type ChannelSettingSummary, MeResponseSchema } from '../../api';
import { useApiClient } from '../../lib/api/api-context';
import { useApiQuery } from '../../lib/api/use-api-query';

import { settingsBySubscription } from './channels-tab-model';

/** What E12 takes from `GET /me`. */
export interface CopiedSettings {
  /** Setting summary by subscription id; empty while loading or after a failure (S9 R4). */
  readonly settings: ReadonlyMap<string, ChannelSettingSummary>;
  readonly reload: () => void;
}

/**
 * `GET /me` read once for the setting summaries of E12 (S9 R4, R10; Proposal, Q122): no polling,
 * only Home polls it (M7 R4). A failure shows nothing: the status chips come from
 * `GET /channels`, and the summary is a detail. The Home feature does not export `useMe`, so E12
 * has its own read, as E11 does (S8).
 */
export function useCopiedSettings(): CopiedSettings {
  const api = useApiClient();
  const load = useCallback(
    (signal: AbortSignal) => api.get('/me', MeResponseSchema, signal),
    [api],
  );
  const me = useApiQuery(load);
  const settings = useMemo(() => settingsBySubscription(me.data), [me.data]);
  return { settings, reload: me.refetch };
}
